import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { Spinner } from 'Components/common/Spinner';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useCountryConfig } from 'hooks/useCountryConfig';
import { useTranslation } from 'react-i18next';
import { getOfficeById } from 'services/offices-service';
import { Main as Container } from 'Components/Main';
import { PageHeader } from 'Components/ui';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';
const BLUE = 'var(--vo-celeste)';
const BORDER = 'var(--vo-cloud)';
const HQ_COLOR = 'var(--vo-cobalto)';
const HQ_BG = 'var(--vo-cobalto-8)';
const ACTIVE_COLOR = 'var(--vo-success)';
const ACTIVE_BG = 'var(--vo-success-10)';
const MUTED_BG = 'var(--vo-slate-10)';

const EditBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  padding: 0 18px;
  background: white;
  color: ${BLUE};
  border: 1.5px solid ${BLUE};
  border-radius: 10px;
  font-family: 'Maven Pro', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { background: var(--vo-capri-50); }
`;

/* ── Summary card ─────────────────────────────────── */
const SummaryCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 20px 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
  margin-bottom: 16px;
  display: flex;
  align-items: flex-start;
  gap: 16px;
`;

const IconTile = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--vo-cobalto), var(--vo-capri));
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  flex: 1;
`;

const SummaryTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

const SummaryName = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 19px;
  font-weight: 700;
  color: ${INK};
  margin: 0;
  letter-spacing: -.01em;
`;

const Badge = styled.span`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${({ $color }) => $color};
  background: ${({ $bg }) => $bg};
  padding: 3px 10px;
  border-radius: 20px;
`;

const SummarySubline = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0;
`;

/* ── Detail card ──────────────────────────────────── */
const DetailCard = styled.div`
  background: white;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;

const DetailCardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${BORDER};
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 20px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

const FullCol = styled.div`
  grid-column: 1 / -1;
`;

const DetailLabel = styled.p`
  font-family: 'Maven Pro', sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: ${MUTED};
  text-transform: uppercase;
  letter-spacing: .07em;
  margin: 0 0 4px;
`;

const DetailValue = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: ${INK};
  margin: 0;
`;

/* ── Loading / error states ───────────────────────── */
const FullLoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px;
`;

const ErrorWrap = styled.div`
  padding: 32px;
  font-family: 'Lato', sans-serif;
  font-size: 14px;
  color: var(--vo-danger);
`;

/* ── SVGs ─────────────────────────────────────────── */
const EditSVG = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const BuildingSVG = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/>
    <line x1="9" y1="9" x2="9" y2="9.01"/><line x1="9" y1="12" x2="9" y2="12.01"/>
    <line x1="9" y1="15" x2="9" y2="15.01"/><line x1="9" y1="18" x2="9" y2="18.01"/>
  </svg>
);

/* ── Component ────────────────────────────────────── */
export const OfficesShow = () => {
  const { t } = useTranslation(['company']);
  const navigate = useNavigate();
  const { id } = useParams();
  const selectedTenant = useSelector(useAuthTenant);
  const { phonePrefix, allConfigs } = useCountryConfig(selectedTenant?.country);

  const {
    isLoading,
    error,
    data: office,
  } = useQuery(['getOfficeId', id], async () => {
    const dataOffice = await getOfficeById(id);
    return dataOffice.status === 200 ? dataOffice.data : null;
  });

  if (isLoading) {
    return (
      <Container>
        <FullLoadingWrap>
          <Spinner $size={24} $borderColor="var(--vo-line)" $speed="0.6s" />
        </FullLoadingWrap>
      </Container>
    );
  }

  if (error || !office) {
    return (
      <Container>
        <ErrorWrap>{t('company:offices.show.error')}</ErrorWrap>
      </Container>
    );
  }

  const isHQ = office.isHeadquarters === true;
  const isActive = office.active !== false;

  const cityState = [office.city, office.state].filter(Boolean).join('/');
  const addressCityState = [office.address, cityState].filter(Boolean).join(', ');
  const phoneDisplay = office.phone ? `${phonePrefix} ${office.phone}` : '';
  const subline = [addressCityState, phoneDisplay].filter(Boolean).join(' · ');
  // office.country is a countryCode (ISO) for records created via the picker; older
  // records may still hold a free-text name — fall back to the raw value so those
  // don't silently disappear.
  const countryLabel = allConfigs.find(c => c.countryCode === office.country)?.name?.pt || office.country || '';

  return (
    <Container>
      <PageHeader
        title={office.name}
        segments={[
          { label: t('common:breadcrumbs.manage') },
          { label: t('company:offices.title'), route: '/offices' },
          { label: office.name },
        ]}
        actions={
          <EditBtn onClick={() => navigate('/offices/edit/' + id)}>
            <EditSVG />
            {t('company:offices.show.edit')}
          </EditBtn>
        }
      />

      <SummaryCard>
        <IconTile>
          <BuildingSVG />
        </IconTile>
        <SummaryInfo>
          <SummaryTitleRow>
            <SummaryName>{office.name}</SummaryName>
            <Badge $color={isHQ ? HQ_COLOR : MUTED} $bg={isHQ ? HQ_BG : MUTED_BG}>
              {isHQ ? t('company:offices.show.matriz') : t('company:offices.show.filial')}
            </Badge>
            <Badge $color={isActive ? ACTIVE_COLOR : MUTED} $bg={isActive ? ACTIVE_BG : MUTED_BG}>
              {isActive ? t('company:offices.show.ativo') : t('company:offices.show.inativo')}
            </Badge>
          </SummaryTitleRow>
          {subline && <SummarySubline>{subline}</SummarySubline>}
        </SummaryInfo>
      </SummaryCard>

      <DetailCard>
        <DetailCardTitle>{t('company:offices.show.cardTitle')}</DetailCardTitle>
        <InfoGrid>
          <div>
            <DetailLabel>{t('company:offices.show.name')}</DetailLabel>
            <DetailValue>{office.name || '—'}</DetailValue>
          </div>
          <div>
            <DetailLabel>{t('company:offices.show.phone')}</DetailLabel>
            <DetailValue>{phoneDisplay || '—'}</DetailValue>
          </div>
          <div>
            <DetailLabel>{t('company:offices.show.responsible')}</DetailLabel>
            <DetailValue>{office.responsibleName || '—'}</DetailValue>
          </div>
          <FullCol>
            <DetailLabel>{t('company:offices.show.address')}</DetailLabel>
            <DetailValue>{office.address || '—'}</DetailValue>
          </FullCol>
          <div>
            <DetailLabel>{t('company:offices.show.city')}</DetailLabel>
            <DetailValue>{office.city || '—'}</DetailValue>
          </div>
          <div>
            <DetailLabel>{t('company:offices.show.state')}</DetailLabel>
            <DetailValue>{office.state || '—'}</DetailValue>
          </div>
          <div>
            <DetailLabel>{t('company:offices.show.country')}</DetailLabel>
            <DetailValue>{countryLabel || '—'}</DetailValue>
          </div>
        </InfoGrid>
      </DetailCard>
    </Container>
  );
};
